import classNames from 'classnames'
import dayjs from 'dayjs'
import i18n, { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Fieldset } from 'primereact/fieldset'
import {
  FileUpload,
  FileUploadHeaderTemplateOptions,
} from 'primereact/fileupload'
import { InputTextarea } from 'primereact/inputtextarea'
import { Messages } from 'primereact/messages'
import { SelectButton } from 'primereact/selectbutton'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { SubCompetencesComponent } from './SubCompetencesComponent'
import {
  API_EDUCATION_REPORT,
  API_EDUCATION_REPORT_BY_APPRENTICE_SEMESTER,
  API_EDUCATION_REPORT_DOWNLOAD,
  API_EDUCATION_REPORT_FILE,
  API_EDUCATION_REPORT_GENERATE,
  API_EDUCATION_REPORT_UPLOAD,
  API_EDUCATION_REPORTS,
  API_OBSERVATIONS_BY_APPRENTICE,
} from '../../../../../constants'
import {
  EducationReport,
  EducationReportDb,
  SubCompetenceAssessment,
} from '../../../../../model/enums/educationReport.model'
import { RequestMethod, Semester } from '../../../../../model/enums/enums'
import { Observation } from '../../../../../model/observation'
import { SignedUrlResponse } from '../../../../../model/signedUrlResponse.model'
import { State } from '../../../../../model/state.model'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'

import { observationsBulkSetEducationReportId } from '../../../../../redux/official-document/education-report/observation/observationReducer'
import { AuthRoutes } from '../../../../../router/routes'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { handleFileUpload } from '../../../../../services/api/handleFileUpload'
import {
  Competence,
  getCompetenceTree,
} from '../../../../../services/competenceService'
import { findApprenticeById } from '../../../../../util/arraySearch'
import { downloadPdf } from '../../../../../util/downloadPdf'
import { getActiveSemester } from '../../../../../util/getActiveSemester'
import { usePrompt } from '../../../../../util/hooks/usePrompt'
import { trimSpaces } from '../../../../../util/trimSpaces'
import { BackButton } from '../../../../util/button/BackButton'
import {
  CompetenceBody,
  SubCompetenceBody,
} from '../../../../util/data-table/cell-body/CompetenceBody'
import {
  RatingBody,
  reasonBody,
} from '../../../../util/data-table/cell-body/RatingBody'
import { AllowPopupDialog } from '../../../../util/dialog/AllowPopupDialog'
import { DeleteConfirmationDialog } from '../../../../util/dialog/DeleteConfirmationDialog'
import { getFormErrorMessage } from '../../../../util/ErrorText'
import { ApprenticeDropdown } from '../../../../util/form/dropdown/person-dropdown/ApprenticeDropdown'
import { EmployeeDropdown } from '../../../../util/form/dropdown/person-dropdown/EmployeeDropdown'
import {
  cancelOptions,
  chooseOptions,
  uploadOptions,
} from '../../../../util/form/MokaiUpload'
import { uploadEmptyTemplate } from '../../../../util/form/UploadEmptyFileTemplate'
import { uploadFileTemplate } from '../../../../util/form/UploadFileTemplate'
import { headerTemplate } from '../../../../util/form/UploadHeader'
import {
  defaultEducationReport,
  formatEducationReport,
  insertObservationsByCompetences,
  insertObservationsIntoCompetence,
} from '../services/educationReportHelper'
import {
  FormError,
  validateEducationReport,
} from '../services/validateEducationReport'

export const EducationReportEntry = (): JSX.Element => {
  const [showPopupBlocked, setShowPopupBlocked] = useState<boolean>(false)
  const dispatcher = useAppDispatch()
  const { loadedBasicInformation } = useAppSelector((state) => state.user)
  const employees = useAppSelector((state) => state.employees)
  const apprentices = useAppSelector((state) => state.apprentices)

  const fileUploadRef = useRef<FileUpload>(null)
  const infoToast = useRef<Toast>(null)
  const infoMsg = useRef<Messages>(null)
  const [editable, setEditable] = useState<boolean>(false)
  const [observationIds, setObservationIds] = useState<number[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const [observationDialog, setShowObservationDialog] = useState<boolean>(false)
  const [isDeleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false)
  const [isUploadDialogVisible, setIsUploadDialogVisible] =
    useState<boolean>(false)
  const [validDropArea, setValidDropArea] = useState<boolean>(false)
  const [competenceTree, setCompetenceTree] = useState<Competence[]>(
    getCompetenceTree[0].children as Competence[]
  )
  const [apprenticeShipDurationInYears, setApprenticeShipDurationInYears] =
    useState<number>(4)
  const [selectedObservations, setSelectedObservations] = useState<
    Observation[] | undefined
  >()
  const [allObservations, setAllObservations] = useState<
    Observation[] | undefined
  >()
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false)
  // this is not a state
  // we do not want to trigger rerender because of this
  let compErrors: string[] = []

  usePrompt(t('core.action.leave_page_warning'), hasUnsavedChanges)

  i18n.on('languageChanged', () => {
    setCompetenceTree([...(getCompetenceTree[0].children as Competence[])])
  })

  const showUploadDialog = () => {
    setIsUploadDialogVisible(true)
  }
  const hideUploadDialog = () => {
    setIsUploadDialogVisible(false)
  }

  const updateLocalObservations = (obsIds: number[], eduId: number) => {
    if (!obsIds || obsIds.length < 1) {
      return
    }
    console.log(
      '[Education Report] Linking the selected observations to the active education report.'
    )
    dispatcher(
      observationsBulkSetEducationReportId({
        observationIds: obsIds,
        educationReportId: eduId,
      })
    )
  }

  const methods = useForm({
    reValidateMode: 'onBlur',
    mode: 'onSubmit',
    defaultValues: defaultEducationReport,
    shouldFocusError: false,
  })

  const updateFormOnWatch = async (
    value: FieldValues,
    name: string,
    shouldEvaluateActiveSemester: boolean
  ) => {
    // we need to reset the observation ids here since we change semester
    setObservationIds([])

    let activeSemester
    if (name === 'id_apprentice' && shouldEvaluateActiveSemester) {
      const apprentice = findApprenticeById(value.id_apprentice, apprentices)
      const apprenticeStart = apprentice?.apprentice?.contract_start
      const apprenticeEnd = apprentice?.apprentice?.contract_end

      if (apprenticeStart && apprenticeEnd) {
        const apprenticeStartDate = new Date(apprenticeStart)
        const apprenticeEndDate = new Date(apprenticeEnd)
        const today = new Date()
        const totalApprenticeshipDurationInYears =
          apprenticeEndDate.getFullYear() - apprenticeStartDate.getFullYear()
        setApprenticeShipDurationInYears(totalApprenticeshipDurationInYears)
        activeSemester = getActiveSemester(
          apprenticeStartDate,
          totalApprenticeshipDurationInYears
        )
        if (today < apprenticeStartDate) {
          activeSemester = 1
        }
        if (today > apprenticeEndDate) {
          activeSemester = totalApprenticeshipDurationInYears * 2
        }
      } else {
        activeSemester = 0
      }
    }

    const selectedId = value.id_apprentice
    const selectedSemester = activeSemester
      ? activeSemester
      : methods.getValues('semester_report')
    const selectedSupervisor = value.id_responsible

    if (hasUnsavedChanges) {
      console.warn('[Education Report] There are unsaved changes registered.')
      // TODO check if values are lost
    }

    console.log('[Education Report] Apprentice change detected.', selectedId)
    if (!selectedId || selectedId === 0) {
      methods.reset(defaultEducationReport)
      methods.setValue('semester_report', selectedSemester)
      methods.setValue('id_responsible', selectedSupervisor)
      setEditable(false)
      console.log('[Education Report] No apprentice selected, not querying.')
      return selectedSemester
    }

    if (!selectedSemester || selectedSemester === 0) {
      methods.reset(defaultEducationReport)
      methods.setValue('id_apprentice', selectedId)
      methods.setValue('id_responsible', selectedSupervisor)
      setEditable(false)
      console.log('[Education Report] No semester selected, not querying.')
      return selectedSemester
    }
    setEditable(true)
    const res = await handleRequest<EducationReportDb[]>(
      API_EDUCATION_REPORT_BY_APPRENTICE_SEMESTER(selectedId, selectedSemester),
      RequestMethod.GET
    )

    if (res.data && res.data.length > 0) {
      console.log(
        '[Education Report] Found an existing education report for ' +
          'the selected apprentice in the selected semester. Loading found data into the form.'
      )
      const eduReport = formatEducationReport(res.data[0])
      methods.reset(eduReport)
      setObservationIds(
        res.data[0].observations ? res.data[0].observations : []
      )
      if (eduReport.signed_file_reference) {
        console.log(
          '[Education Report] ' +
            'Disabling edit since the file has already a signed file reference.'
        )
        setEditable(false)
      }
      // there is nothing
    } else {
      methods.reset(defaultEducationReport)
      methods.setValue('id_apprentice', selectedId, { shouldDirty: true })
      methods.setValue('semester_report', selectedSemester, {
        shouldDirty: true,
      })
      methods.setValue('id_responsible', undefined, { shouldDirty: true })
    }
    return selectedSemester
  }

  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      if (type === 'change') {
        // apprentice id and semester are used to load and reference the report.
        // If anything else is changed it is considered to be a unsaved value the editor wants to keep.\
        if (name !== 'semester_report' && name !== 'id_apprentice') {
          setHasUnsavedChanges(true)
          return
        }
        // as soon as we change semester or apprentice we lose the progress anyway
        else {
          setHasUnsavedChanges(false)
        }

        const selectedSemester = await updateFormOnWatch(value, name, true)

        if (value.id_apprentice && selectedSemester) {
          window.history.replaceState(
            null,
            '',
            `${AuthRoutes.educationReports}/${value.id_apprentice}/${selectedSemester}`
          )
        } else if (value.id_apprentice) {
          window.history.replaceState(
            null,
            '',
            `${AuthRoutes.educationReports}/${value.id_apprentice}`
          )
        }
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch])

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (location) {
      console.info('[Education Report] Registered route change.')
      const base_url = window.location.origin + AuthRoutes.educationReports
      console.log(base_url)
      // the location is at this point ...Company/<companyId> and we want to extract the Company id.
      // Reason: If someone sends this link we want to display the data of this url
      const substrings = location.pathname.split('/')
      const lastValue = substrings[substrings.length - 1]
      const secondLastValue = substrings[substrings.length - 2]
      let apprenticeID
      let semesterID
      if (secondLastValue === AuthRoutes.educationReports.substring(1)) {
        apprenticeID = parseInt(lastValue)
      } else {
        semesterID = parseInt(lastValue)
        apprenticeID = parseInt(secondLastValue)
      }
      // get the index of the previous

      if (!apprenticeID) {
        console.warn(
          '[Education Report] ApprenticeId not defined. something went wrong!'
        )
        return
      }

      const apprentice = findApprenticeById(apprenticeID, apprentices)

      if (!apprentice) {
        console.warn(
          '[Education Report] ApprenticeId not found. something went wrong!'
        )
        return
      }

      const apprenticeStart = apprentice?.apprentice?.contract_start
      const apprenticeEnd = apprentice?.apprentice?.contract_end
      let semesterCorrect = false
      if (semesterID) {
        if (apprenticeStart && apprenticeEnd) {
          const apprenticeStartDate = new Date(apprenticeStart)
          const apprenticeEndDate = new Date(apprenticeEnd)
          const totalApprenticeshipDurationInYears =
            apprenticeEndDate.getFullYear() - apprenticeStartDate.getFullYear()
          if (
            totalApprenticeshipDurationInYears * 2 >= semesterID &&
            semesterID <= 8
          ) {
            setApprenticeShipDurationInYears(totalApprenticeshipDurationInYears)
            methods.setValue('semester_report', semesterID, {
              shouldDirty: true,
            })
            semesterCorrect = true
          }
        }

        if (!semesterCorrect) {
          navigate(`${AuthRoutes.educationReports}/${apprenticeID}`)
        }
      }
      methods.setValue('id_apprentice', apprenticeID)
      updateFormOnWatch(methods.getValues(), 'id_apprentice', !semesterCorrect)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, loadedBasicInformation])

  const concatComps = (comps: any): SubCompetenceAssessment[] => {
    const concatenatedSubCompetences: SubCompetenceAssessment[] =
      comps[0].sub_competence_assessment
        .concat(comps[1].sub_competence_assessment)
        .concat(comps[2].sub_competence_assessment)
        .concat(comps[3].sub_competence_assessment)
        .concat(comps[4].sub_competence_assessment)
        .concat(comps[5].sub_competence_assessment)
    const filledCompetences: SubCompetenceAssessment[] = []

    concatenatedSubCompetences.forEach(
      (subCompetence: SubCompetenceAssessment) => {
        subCompetence.id_education_report = methods.getValues(
          'education_report_id'
        )
        if (subCompetence.reason || subCompetence.rating > 0) {
          filledCompetences.push(subCompetence)
        }

        // if we remove the rating it becomes null
        if (!subCompetence.rating) {
          subCompetence.rating = 0
        }

        // this happens when we delete existing rating and reason on a saved competence
        if (
          !subCompetence.rating &&
          !subCompetence.reason &&
          subCompetence.sub_competence_assessment_id
        ) {
          filledCompetences.push({ ...subCompetence, delete: true })
        }

        if (subCompetence.rating <= 0 && subCompetence.reason) {
          console.warn(
            `[Education Report] Losing data for sub-competence with id ${subCompetence.id_sub_competence}.
		            Because no rating is set but a reason.`
          )
          compErrors.push(subCompetence.reason)
        }
      }
    )
    return filledCompetences
  }

  const deleteUploadedFile = async () => {
    const reportId = methods.getValues('education_report_id')
    if (!reportId || reportId === 0) {
      return
    }
    const result = await handleRequest(
      API_EDUCATION_REPORT_FILE(reportId),
      RequestMethod.DELETE
    )
    if (result.httpStatus === 200) {
      methods.setValue('signed_file_reference', undefined)
      setEditable(true)
    }
  }

  // Open the delete dialog if reports are selected
  const openDeleteDialog = () => {
    setDeleteDialogVisible(true)
  }

  // close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogVisible(false)
  }

  const onSubmit = async (report: EducationReport): Promise<number> => {
    const concatSubComps = concatComps(report.competence)
    const body: EducationReportDb = {
      education_report_id: report.education_report_id,
      id_apprentice: report.id_apprentice,
      semester_report: report.semester_report,
      course_agreement: trimSpaces(report.course_agreement),
      discussion_date: report.discussion_date,
      education_person_ct: report.education_person_ct,
      feedback_apprenticeship_by_apprentice: trimSpaces(
        report.feedback_apprenticeship_by_apprentice
      ),
      feedback_responsible_by_apprentice: trimSpaces(
        report.feedback_responsible_by_apprentice
      ),
      miscellaneous: trimSpaces(report.miscellaneous),
      signed_file_reference: report.signed_file_reference,
      submission_date: report.submission_date
        ? dayjs(report.submission_date).format('YYYY-MM-DD').toString()
        : undefined,
      closing_date: report.closing_date
        ? dayjs(report.closing_date).format('YYYY-MM-DD').toString()
        : undefined,
      is_active: report.is_active,
      last_change: report.last_change,
      target_achievement_note: report.target_achievement_note,
      sub_competence_assessment: concatSubComps,
      observations: observationIds,
      id_responsible: report.id_responsible,
    }

    if (compErrors.length > 0) {
      let lostTexts = compErrors.join('\r\n')
      if (lostTexts.length > 50) {
        lostTexts = `${lostTexts.slice(0, 50)}...`
      }

      if (lostTexts) {
        infoToast.current?.show({
          closable: true,
          severity: 'error',
          summary: t<string>(
            'education_report.error.add_subcompetence_wo_rating_sum'
          ),
          detail:
            `${t<string>(
              'education_report.error.add_subcompetence_wo_rating'
            )}: \n` + lostTexts,
          life: 12000,
        })
      }
      compErrors = []
      return -1
    }

    const formErrors = validateEducationReport(body)
    if (formErrors?.length > 0) {
      console.warn(
        '[Education Report] The education report has errors',
        formErrors
      )

      let errorText = formErrors.map((e: FormError) => {
        const translatedKey = t<string>(`education_report.keys.${e.key}`)
        const errorType = t<string>(
          `education_report.error.type.${e.errorType}`,
          { value: e.errorValue }
        )
        return `${translatedKey}: ${errorType}\r\n`
      })

      infoToast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('education_report.error.summary'),
        detail: errorText,
        life: 12000,
      })
      return -1
    }

    // existent
    let edReportId: number = -1
    if (body.education_report_id) {
      console.log('[Education Report] Updating existing report.')

      const res = await handleRequest<EducationReportDb>(
        API_EDUCATION_REPORT(body.education_report_id),
        RequestMethod.PUT,
        body
      )

      if (res?.data?.education_report_id) {
        const formattedReport = formatEducationReport(res.data)
        // do we need this?
        methods.reset(formattedReport)
        updateLocalObservations(
          res.data.observations,
          res.data.education_report_id
        )
        setHasUnsavedChanges(false)
        edReportId = res.data.education_report_id
      }

      //non-existent
    } else {
      console.log('[Education Report] Adding new report.')
      const res = await handleRequest<EducationReportDb>(
        API_EDUCATION_REPORTS(),
        RequestMethod.POST,
        body
      )

      if (!res?.data?.education_report_id) {
        console.warn('[Education Report] adding the report was not successful.')
        return -1
      }
      //methods.setValue('education_report_id', res.data?.education_report_id, {shouldDirty: false})
      const formattedReport = formatEducationReport(res.data)
      // do we need this?
      methods.reset(formattedReport)
      if (body.observations?.length > 0) {
        updateLocalObservations(
          body.observations,
          res.data?.education_report_id
        )
      }
      setHasUnsavedChanges(false)
      edReportId = res.data.education_report_id
    }

    infoToast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.save.success.title'),
      detail: t<string>('core.action.save.success.message'),
      life: 4000,
    })

    return edReportId
  }

  const onHideObservationDialog = () => {
    setShowObservationDialog(false)
    setSelectedObservations(undefined)
    setAllObservations(undefined)
  }

  const insertObservationByComp = insertObservationsByCompetences(
    methods,
    employees
  )

  const insertObservations = insertObservationsIntoCompetence(
    setHasUnsavedChanges,
    insertObservationByComp,
    methods
  )

  const supplementSelectedObservations = () => {
    console.log(
      `[Education Report] User choose to append ${
        selectedObservations ? selectedObservations.length : '0'
      } 
            selected observations.`
    )
    const newObsIDs = insertObservations(selectedObservations)
    let currObsIDs = observationIds
    if (newObsIDs) {
      currObsIDs = currObsIDs.concat(newObsIDs)
      setObservationIds(currObsIDs)
      methods.setValue('observations', currObsIDs)
    }
    onHideObservationDialog()
  }

  const semester = [
    {
      value: Semester.sem1,
      label: t<string>(`core.education.sem${Semester.sem1}`),
    },
    {
      value: Semester.sem2,
      label: t<string>(`core.education.sem${Semester.sem2}`),
    },
    {
      value: Semester.sem3,
      label: t<string>(`core.education.sem${Semester.sem3}`),
    },
    {
      value: Semester.sem4,
      label: t<string>(`core.education.sem${Semester.sem4}`),
    },
    {
      value: Semester.sem5,
      label: t<string>(`core.education.sem${Semester.sem5}`),
    },
    {
      value: Semester.sem6,
      label: t<string>(`core.education.sem${Semester.sem6}`),
    },
    {
      value: Semester.sem7,
      label: t<string>(`core.education.sem${Semester.sem7}`),
    },
    {
      value: Semester.sem8,
      label: t<string>(`core.education.sem${Semester.sem8}`),
    },
  ]
  const fourYear = semester
  const threeYear = semester.slice(0, -2)
  const twoYear = semester.slice(0, -4)

  const subCompetences = SubCompetencesComponent(methods, editable)

  const observationFooter = () => {
    // the skip button is probably not needed
    return (
      <>
        <Button
          className={'p-button-secondary p-button-text'}
          label={t<string>('core.skip')}
          onClick={() => setShowObservationDialog(false)}
        />
        <Button
          className={'p-button-primary mr-0'}
          disabled={!selectedObservations || selectedObservations?.length === 0}
          label={t<string>('education_report.use_observations')}
          onClick={() => supplementSelectedObservations()}
        />
      </>
    )
  }

  const openObservationDialog = async () => {
    setShowObservationDialog(true)
    const allObservationsOfApprenticeResult = await handleRequest<
      Observation[]
    >(
      API_OBSERVATIONS_BY_APPRENTICE(methods.getValues('id_apprentice')),
      RequestMethod.GET
    )

    if (
      allObservationsOfApprenticeResult?.data &&
      allObservationsOfApprenticeResult.data.length > 0
    ) {
      console.log(
        '[Education Report] Unused observations for the selected apprentice are found. Show prompt.'
      )
      let unusedObservations = allObservationsOfApprenticeResult.data.filter(
        (x) => x.id_education_report === null
      )
      // this is for the case that we have already chosen that
      unusedObservations = unusedObservations.filter((x) => {
        if (observationIds && x.observation_id) {
          if (observationIds.includes(x.observation_id)) return false
        }
        return true
      })
      setSelectedObservations(unusedObservations)
      setAllObservations(unusedObservations)
    }
    console.log(
      '[Education Report] No additional Observation for the selected apprentice found.'
    )
  }

  const handleUploadResultOfReport = (id: number | undefined): void => {
    if (id === undefined) {
      console.log(
        '[Education Report] Something went wrong uploading the report.'
      )
      return
    }
    console.log('[Education Report] Successfully uploaded the report.')
    methods.setValue('signed_file_reference', 'created')
    setEditable(false)
    hideUploadDialog()
  }

  const getUploadUrl = () => {
    const eduIp = methods.getValues('education_report_id')
    if (!eduIp) {
      return undefined
    }
    return API_EDUCATION_REPORT_UPLOAD(eduIp)
  }

  const fileUploadProps = {
    handleUploadResult: handleUploadResultOfReport,
    uploadUrl: getUploadUrl(),
    documentId: methods.getValues('education_report_id'),
  }

  function handleValidationFail(error: any) {
    console.warn('validation failed', error)
  }

  const onTemplateRemove = (file: any, callback: any) => {
    callback()
  }

  const itemTemplate = uploadFileTemplate(onTemplateRemove)

  const generateReport = async (save: boolean) => {
    // this works because if we have save === false, the report must already exist
    const res = save
      ? await onSubmit(methods.getValues())
      : methods.getValues('education_report_id')
    if (!res || res <= 0) {
      console.warn(
        '[Education Report] ' +
          'Education report id not set. This function should not be callable at this state.'
      )
      return
    }

    setIsGeneratingPDF(true)
    const response: State<any> = await handleRequest(
      API_EDUCATION_REPORT_GENERATE(res),
      RequestMethod.GET_PDF
    )
    const sem = methods.getValues('semester_report')
    const apprentice = findApprenticeById(
      methods.getValues('id_apprentice'),
      apprentices
    )
    const fileName =
      `${apprentice?.first_name}-${apprentice?.last_name}-` +
      `${t<string>('education_report.file_name')}-${sem}`
    downloadPdf(response, fileName)
    setIsGeneratingPDF(false)
  }

  const openSignedFile = async () => {
    const id = methods.getValues('education_report_id')
    if (!id) {
      return
    }
    const downloadLinkResult = await handleRequest<SignedUrlResponse>(
      API_EDUCATION_REPORT_DOWNLOAD(id),
      RequestMethod.GET
    )

    const signedUrl = downloadLinkResult?.data?.url
    if (signedUrl) {
      const newWindow: Window | null = window.open(signedUrl, '_blank')

      if (newWindow) {
        newWindow.location = signedUrl
        newWindow.opener = null
        return
      } else {
        console.warn('Popup blocked!')
        setShowPopupBlocked(true)
      }
    }
  }

  return (
    <div className={'card pl-2 pr-2'}>
      <BackButton
        translationKey={'navigation.go_back_overview'}
        route={AuthRoutes.educationReports}
      />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={'p-fluid formgrid grid pl-2'}>
            <div className={' col-12 formgroup-inline grid mt-1'}>
              <div className={'md:col-6 col-12 mb-0 pr-2'}>
                <ApprenticeDropdown />
              </div>
              <div className={'md:col-6 col-12 mb-0 pr-2'}>
                <EmployeeDropdown fieldName={'id_responsible'} />
              </div>
            </div>
            <div className={'formgroup-inline ml-0 col-12'}>
              <label htmlFor={'semester-button'} className={'col-12 pl-0 pt-2'}>
                {t<string>('core.education.semester')}
              </label>
              <Controller
                name="semester_report"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <SelectButton
                    className={classNames(
                      { 'p-invalid': fieldState.error },
                      'col-12 md:col-6 m-0 mb-2 pt-0 pl-0'
                    )}
                    id={field.name}
                    {...field}
                    options={
                      apprenticeShipDurationInYears === 3
                        ? threeYear
                        : apprenticeShipDurationInYears === 2
                        ? twoYear
                        : fourYear
                    }
                    disabled={
                      methods.getValues('id_apprentice') === undefined ||
                      methods.getValues('id_apprentice') < 1
                    }
                  />
                )}
              />
              <div className={'col pt-2 formgroup-inline grid'}>
                {methods.getValues('signed_file_reference') ? (
                  <div className={'grid col m-0 p-0 '}>
                    <div className={'col ml-0 pl-0'}>
                      <Button
                        label={t<string>(
                          'education_report.download_signed_report'
                        )}
                        type={'button'}
                        icon="fas fa-file-pdf"
                        className=" p-button-warning"
                        onClick={() => openSignedFile()}
                      />
                    </div>
                    <Button
                      type={'button'}
                      icon="fa-solid fa-trash"
                      className="col-2 m-0 p-0 p-button-danger p-button-text"
                      onClick={() => openDeleteDialog()}
                      tooltip={t<string>(
                        'education_report.delete_file.tooltip'
                      )}
                      tooltipOptions={{
                        appendTo: document.body,
                        position: 'top',
                        showDelay: 500,
                      }}
                    />
                  </div>
                ) : (
                  <div className={'col pl-0 pr-1 mb-2'}>
                    <Button
                      label={t<string>('education_report.upload')}
                      type={'button'}
                      icon="fas fa-file-upload"
                      className="p-button"
                      onClick={() => showUploadDialog()}
                      disabled={!methods.getValues('education_report_id')}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={'col-6 md:col-6 mb-2 pl-0 ml-auto mr-1'}>
              <Button
                type="button"
                label={t<string>('education_report.show_observation_dialog')}
                onClick={openObservationDialog}
                className={'p-button-outlined'}
                disabled={
                  methods.getValues('id_apprentice') === undefined ||
                  methods.getValues('id_apprentice') < 1 ||
                  // @TODO, this is not the fine way
                  // needs to check for empty string, undefined, null
                  !!methods.getValues('signed_file_reference')
                }
              />
            </div>
            <div className={'col-6 p-0 pl-2'}>
              <Messages ref={infoMsg} />
              {/*ref={infoMsg}*/}
            </div>
            <div className={'col-12 md:col-12 col-12'}>
              {competenceTree?.map(
                (competence: Competence, competenceIndex: number) => {
                  return (
                    <Fieldset
                      legend={`${competence.key}. ${t<string>(
                        competence.label
                      )}`}
                      key={'fs_' + competenceIndex}
                      className={'mb-4'}
                      toggleable
                    >
                      <div className={'md:col-12 col-12 formgroup-inline grid'}>
                        <div className={'md:col-12 p-0 m-0'}>
                          {subCompetences(competence, competenceIndex)}
                        </div>
                      </div>
                    </Fieldset>
                  )
                }
              )}

              <Fieldset
                legend={t<string>('education_report.feedback_title')}
                key={'fs_feedback'}
                className={'mb-4'}
                toggleable
              >
                <label>
                  {t<string>('education_report.feedback_apprenticeship')}
                </label>
                <Controller
                  name={'feedback_apprenticeship_by_apprentice'}
                  control={methods.control}
                  rules={{ maxLength: 300 }}
                  render={({ field }) => (
                    <InputTextarea
                      {...field}
                      autoResize={true}
                      disabled={!editable}
                      maxLength={300}
                      className={classNames({
                        'p-error':
                          methods.formState.errors
                            .feedback_apprenticeship_by_apprentice,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage(
                  methods.formState.errors,
                  'feedback_apprenticeship_by_apprentice'
                )}
                <label>
                  {t<string>('education_report.feedback_responsible')}
                </label>
                <Controller
                  name={'feedback_responsible_by_apprentice'}
                  control={methods.control}
                  rules={{ maxLength: 300 }}
                  render={({ field }) => (
                    <InputTextarea
                      {...field}
                      autoResize={true}
                      disabled={!editable}
                      maxLength={300}
                      className={classNames({
                        'p-error':
                          methods.formState.errors
                            .feedback_responsible_by_apprentice,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage(
                  methods.formState.errors,
                  'feedback_responsible_by_apprentice'
                )}
              </Fieldset>

              <Fieldset
                legend={t<string>('education_report.target_achievement_note')}
                key={'fs_achievement_note'}
                className={'mb-2'}
                toggleable
              >
                <p className={'secondary-text small-text'}>
                  {t<string>('education_report.target_achievement_note_hint')}
                </p>
                <Controller
                  name={'target_achievement_note'}
                  control={methods.control}
                  rules={{ maxLength: 300 }}
                  render={({ field }) => (
                    <InputTextarea
                      {...field}
                      autoResize={true}
                      disabled={!editable}
                      maxLength={300}
                      className={classNames({
                        'p-error':
                          methods.formState.errors.target_achievement_note,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage(
                  methods.formState.errors,
                  'target_achievement_note'
                )}
              </Fieldset>
              <Fieldset
                legend={t<string>('education_report.target_agreement')}
                key={'fs_target_agreement'}
                className={'mb-2 gray-scale'}
                toggleable
              >
                <span className={'secondary-text'}>
                  {t<string>('core.fill_after_generation')}
                </span>
              </Fieldset>

              <Fieldset
                legend={t<string>('education_report.course_agreement')}
                key={'fs_course_agreement'}
                className={'mb-2'}
                toggleable
              >
                <Controller
                  name={'course_agreement'}
                  control={methods.control}
                  rules={{ maxLength: 300 }}
                  render={({ field }) => (
                    <InputTextarea
                      {...field}
                      autoResize={true}
                      disabled={!editable}
                      maxLength={300}
                      className={classNames({
                        'p-error': methods.formState.errors.course_agreement,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage(
                  methods.formState.errors,
                  'course_agreement'
                )}
              </Fieldset>
              <Fieldset
                legend={t<string>('education_report.miscellaneous')}
                key={'fs_miscellaneous'}
                className={'mb-2'}
                toggleable
              >
                <Controller
                  name={'miscellaneous'}
                  control={methods.control}
                  rules={{ maxLength: 300 }}
                  render={({ field }) => (
                    <InputTextarea
                      {...field}
                      autoResize={true}
                      disabled={!editable}
                      maxLength={300}
                    />
                  )}
                />
              </Fieldset>
            </div>
          </div>
          <div className={'mt-2 mb-2 flex gap-2 justify-content-end'}>
            <Button
              type={'button'}
              label={
                !hasUnsavedChanges && methods.getValues('education_report_id')
                  ? t<string>('core.create_pdf')
                  : t<string>('core.save_create_pdf')
              }
              className={`${
                methods.getValues('signed_file_reference')
                  ? 'p-button-help'
                  : 'p-button-warning'
              }`}
              loading={isGeneratingPDF}
              onClick={() =>
                generateReport(
                  hasUnsavedChanges || !methods.getValues('education_report_id')
                )
              }
              disabled={
                !editable ||
                (!hasUnsavedChanges &&
                  !methods.getValues('education_report_id'))
              }
            />
            {methods.getValues('signed_file_reference') ? (
              <>
                <Button
                  label={t<string>('education_report.download_signed_report')}
                  icon="fas fa-file-download "
                  type={'button'}
                  onClick={() => openSignedFile()}
                  className="p-button-warning"
                />
                <Button
                  type={'button'}
                  icon="fa-solid fa-trash fa-lg"
                  onClick={() => openDeleteDialog()}
                  className="p-button-rounded p-button-danger p-button-text"
                  tooltip={t<string>('education_report.delete_file.tooltip')}
                  tooltipOptions={{
                    appendTo: document.body,
                    position: 'top',
                    showDelay: 500,
                  }}
                />
              </>
            ) : (
              <>
                <Button
                  label={t<string>('education_report.upload')}
                  type={'button'}
                  className="p-button-text"
                  disabled={!methods.getValues('education_report_id')}
                  onClick={() => showUploadDialog()}
                />
                <Button
                  type="submit"
                  label={t<string>('core.save_changes')}
                  disabled={!editable || !hasUnsavedChanges}
                />
              </>
            )}
          </div>
        </form>
      </FormProvider>
      <Dialog
        onHide={onHideObservationDialog}
        id={'observation_dialog'}
        header={`${t<string>('title.observations')} ${t<string>(
          'core.supplement'
        )} `}
        visible={observationDialog}
        footer={observationFooter}
      >
        <p className={'mb-2'}>
          {t<string>('education_report.add_additional_observations', {
            number: selectedObservations?.length,
          })}{' '}
          <Tooltip
            target={'.additional_obs_explanation'}
            appendTo={document.body}
            content={t<string>(
              'education_report.add_additional_observations_help'
            )}
          />{' '}
          <span
            className={
              'ml-2 fa-solid fa-info-circle additional_obs_explanation icon-blue'
            }
          />
        </p>

        <DataTable
          dataKey={'observation_id'}
          className={'p-datatable-sm'}
          selectionMode={'multiple'}
          selectAll={true}
          value={allObservations}
          emptyMessage={t<string>('core.empty_message')}
          selection={selectedObservations}
          resizableColumns
          stripedRows
          rowHover
          size={'small'}
          scrollHeight={'30vh'}
          columnResizeMode={'fit'}
          onSelectionChange={(e) => setSelectedObservations(e.value)}
        >
          <Column selectionMode="multiple" />
          <Column
            header={t<string>('observation.created_on')}
            field={'created_on'}
            body={(obs: Observation) =>
              dayjs(obs.created_on).format(t<string>('locales.dateFormat'))
            }
          />
          <Column
            header={t<string>('observation.competence')}
            field={'id_sub_competence'}
            body={(r) => CompetenceBody(r, competenceTree)}
          />
          <Column
            header={t<string>('observation.sub_competence')}
            field={'id_competence_detail'}
            body={(r) => SubCompetenceBody(r, competenceTree)}
          />
          <Column
            header={t<string>('observation.rating')}
            field={'rating'}
            body={RatingBody}
          />
          <Column
            header={t<string>('observation.reason')}
            field={'reason'}
            body={reasonBody}
          />
        </DataTable>
      </Dialog>
      <DeleteConfirmationDialog
        onClose={closeDeleteDialog}
        onDelete={deleteUploadedFile}
        visible={isDeleteDialogVisible}
        title={t<string>('education_report.delete_file.title')}
        message={t<string>('education_report.delete_file.message')}
      />
      <Dialog
        onHide={hideUploadDialog}
        id={'upload_dialog'}
        header={`${t<string>('title.education_report')} ${t<string>(
          'core.upload'
        )} `}
        visible={isUploadDialogVisible}
      >
        <FileUpload
          ref={fileUploadRef}
          name="educationReport"
          customUpload={true}
          headerTemplate={(options: FileUploadHeaderTemplateOptions) =>
            headerTemplate(options, true)
          }
          chooseOptions={chooseOptions}
          uploadOptions={uploadOptions}
          cancelOptions={cancelOptions}
          emptyTemplate={uploadEmptyTemplate(setValidDropArea, validDropArea)}
          itemTemplate={itemTemplate}
          contentStyle={{ backgroundColor: 'rgb(226, 227,227)' }}
          multiple={false}
          onValidationFail={handleValidationFail}
          accept={'.pdf'}
          maxFileSize={10000000}
          uploadHandler={handleFileUpload(fileUploadProps)}
          disabled={
            methods.getValues('education_report_id') === undefined ||
            methods.getValues('education_report_id') === 0 ||
            !editable
          }
        />
      </Dialog>
      <AllowPopupDialog
        closeDialog={() => setShowPopupBlocked(false)}
        showDialog={showPopupBlocked}
      />
      <Toast ref={infoToast} position={'bottom-right'} />
    </div>
  )
}